import { forwardRef, useImperativeHandle, useState } from 'react';
import { getNeedsAttentionChallenges } from '../../../../services/dashboard.service';
import { CreatorChallenge } from '../../../../types/challenges';
import useCursorChallenges from '../../../../hooks/useCursorChallenges';
import useIsMobile from '../../../../hooks/useIsMobile';
import ThreeDotsLoader from '../../../common/three-dots-loader/three-dots-loader';
import ChallengeRow from '../ChallengeRow';
import ActiveChallengeDrawer from './ActiveChallengeDrawer';
import styles from './allChallenges.module.css';

export type ChallengesRefetchHandle = {
  refetch: () => void;
};

type ActiveChallengesListProps = {
  defaultCurrency: string;
  brandName?: string;
  onClaimReward: (challenge: CreatorChallenge) => void;
};

const ActiveChallengesList = forwardRef<ChallengesRefetchHandle, ActiveChallengesListProps>(
  ({ defaultCurrency, brandName, onClaimReward }, ref) => {
    const isMobile = useIsMobile();
    const [drawerChallenge, setDrawerChallenge] = useState<CreatorChallenge | null>(null);
    const { challenges, isLoading, isLoadingMore, listRef, handleScroll, refetch } =
      useCursorChallenges(getNeedsAttentionChallenges);

    useImperativeHandle(ref, () => ({ refetch }));

    if (isLoading) {
      return (
        <div className={styles.loaderContainer}>
          <ThreeDotsLoader />
        </div>
      );
    }

    if (challenges.length === 0) {
      return (
        <div className={styles.emptyState}>
          <div className={styles.emptyStateIcon}>🏆</div>
          <p>{brandName ? `${brandName} has not created any challenges yet.` : 'No active challenges right now.'}</p>
        </div>
      );
    }

    return (
      <>
        <div className={styles.activeList} ref={listRef} onScroll={handleScroll}>
          {challenges.map((challenge) => (
            <ChallengeRow
              key={challenge.id}
              challenge={challenge}
              defaultCurrency={defaultCurrency}
              onClaimReward={onClaimReward}
              onOpenDetails={isMobile ? setDrawerChallenge : undefined}
            />
          ))}
          {isLoadingMore && (
            <div className={styles.loadMoreContainer}>
              <ThreeDotsLoader />
            </div>
          )}
        </div>
        {isMobile && (
          <ActiveChallengeDrawer
            challenge={drawerChallenge}
            defaultCurrency={defaultCurrency}
            onClose={() => setDrawerChallenge(null)}
            onClaimReward={onClaimReward}
          />
        )}
      </>
    );
  }
);

ActiveChallengesList.displayName = 'ActiveChallengesList';

export default ActiveChallengesList;
